import { useState, useEffect } from 'react';
import API from '@/lib/api';
import IOSToggle from '@/components/IOSToggle';
import { PenNib, CheckCircle } from '@phosphor-icons/react';

export default function LeverbonPanel() {
  const [form, setForm] = useState({
    leverbon_handtekening_actief: true,
    leverbon_handtekening_verplicht: false,
    leverbon_handtekening_tekst: 'Voor ontvangst in goede staat getekend door:',
    leverbon_toon_prijzen: false,
    leverbon_teken_link_uren: 48,
    leverbon_teken_notify_email: true,
    leverbon_teken_notify_adres: '',
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (k) => (v) => setForm(s => ({ ...s, [k]: typeof v === 'object' && v?.target ? v.target.value : v }));

  useEffect(() => {
    API.get('/instellingen').then(r => {
      const d = r.data || {};
      setForm(s => ({
        ...s,
        leverbon_handtekening_actief: d.leverbon_handtekening_actief ?? true,
        leverbon_handtekening_verplicht: d.leverbon_handtekening_verplicht ?? false,
        leverbon_handtekening_tekst: d.leverbon_handtekening_tekst || s.leverbon_handtekening_tekst,
        leverbon_toon_prijzen: d.leverbon_toon_prijzen ?? false,
        leverbon_teken_link_uren: d.leverbon_teken_link_uren || 48,
        leverbon_teken_notify_email: d.leverbon_teken_notify_email ?? true,
        leverbon_teken_notify_adres: d.leverbon_teken_notify_adres || '',
      }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await API.put('/instellingen', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="leverbon-panel">
      <div className="flex items-start gap-4 p-4 rounded-lg border border-green-200" style={{ background: 'linear-gradient(90deg,#F0FDF4 0%,#ECFDF5 100%)' }}>
        <div className="p-2 rounded bg-white border border-green-200"><PenNib size={28} className="text-green-700" /></div>
        <div className="flex-1">
          <div className="font-bold text-green-800 mb-1">Leverbon</div>
          <div className="text-xs text-gray-700">Instellingen voor leveringsbonnen, waaronder digitale handtekening van de klant op tablet of via een smartphone-link.</div>
        </div>
      </div>

      <div className="section-card">
        <div className="section-header"><span className="section-title">Digitale handtekening</span></div>
        <div className="section-body space-y-3">
          <IOSToggle
            label="Handtekening-pad toegankelijk op elke leverbon"
            checked={form.leverbon_handtekening_actief}
            onChange={update('leverbon_handtekening_actief')}
            testid="lb-ht-actief"
          />
          <p className="text-[11px] text-gray-500 pl-7 -mt-2">Wanneer UIT: geen handtekening-sectie op leverbon-pagina's. U kan dit ook per leverbon aan/uit zetten.</p>

          <IOSToggle
            label="Handtekening vereist voor status &laquo;Geleverd&raquo;"
            checked={form.leverbon_handtekening_verplicht}
            onChange={update('leverbon_handtekening_verplicht')}
            testid="lb-ht-verplicht"
          />
          <p className="text-[11px] text-gray-500 pl-7 -mt-2">Automatische status-overgang: zodra klant tekent → status = &laquo;geleverd&raquo;.</p>

          <div>
            <label className="form-label">Tekst boven de handtekening (op PDF)</label>
            <input
              className="field-input"
              value={form.leverbon_handtekening_tekst}
              onChange={update('leverbon_handtekening_tekst')}
              placeholder="Voor ontvangst in goede staat getekend door:"
              data-testid="lb-ht-tekst"
            />
          </div>

          <div>
            <label className="form-label">Geldigheid teken-link</label>
            <div className="text-[11px] text-gray-600 bg-green-50 border border-green-200 px-2.5 py-1.5 rounded" data-testid="lb-ht-geldigheid-info">
              ♾️ <strong>Onbeperkt geldig</strong> — de smartphone-teken-link blijft actief tot de klant tekent. Zodra ondertekend, wordt de link automatisch ongeldig (kan geen 2× gebruikt worden).
            </div>
          </div>
        </div>
      </div>

      <div className="section-card">
        <div className="section-header"><span className="section-title">Notificatie bij ondertekening</span></div>
        <div className="section-body space-y-3">
          <IOSToggle
            label="E-mail mij automatisch wanneer klant een leverbon ondertekent"
            checked={form.leverbon_teken_notify_email}
            onChange={update('leverbon_teken_notify_email')}
            testid="lb-notify-email"
          />
          <p className="text-[11px] text-gray-500 pl-7 -mt-2">U ontvangt de ondertekende PDF als bijlage zodra uw klant via de smartphone-link of tablet heeft getekend. Vereist dat SendGrid geconfigureerd is.</p>

          <div>
            <label className="form-label">Ontvanger-adres <span className="text-gray-400 text-[10px]">(optioneel — standaard gaat het naar uw bedrijfs-e-mail)</span></label>
            <input
              type="email"
              className="field-input"
              value={form.leverbon_teken_notify_adres}
              onChange={update('leverbon_teken_notify_adres')}
              placeholder="administratie@uwbedrijf.be"
              data-testid="lb-notify-adres"
            />
          </div>
        </div>
      </div>

      <div className="section-card">
        <div className="section-header"><span className="section-title">Weergave</span></div>
        <div className="section-body space-y-3">
          <IOSToggle
            label="Prijzen tonen op leverbon-PDF"
            checked={form.leverbon_toon_prijzen}
            onChange={update('leverbon_toon_prijzen')}
            testid="lb-toon-prijzen"
          />
          <p className="text-[11px] text-gray-500 pl-7 -mt-2">Klassiek: leverbonnen tonen enkel aantallen zonder prijzen. Activeer voor interne leverbonnen waar u prijzen wel wilt tonen.</p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
        {saved && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Opgeslagen</span>}
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy} data-testid="lb-save">
          {busy ? 'Opslaan…' : 'Opslaan'}
        </button>
      </div>
    </div>
  );
}
